import {
    ArrowDownOutlined,
    ArrowUpOutlined,
    BarChartOutlined,
    ClockCircleOutlined,
    NumberOutlined,
} from "@ant-design/icons";
import {
    Card,
    Col,
    DatePicker,
    Input,
    Row,
    Select,
    Table,
    Tag,
    Tooltip as AntTooltip,
    Typography,
} from "antd";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import HTTP from "../../services/httpService";
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { useThemeMode } from "../../context/ThemeContext";
import {
    generatePerformanceTransactions,
    summarizeTransactions,
} from "./mockPerformanceData";
import CustomHeader from "../common/Header";
import "./PerformanceInsights.css";

const { Title, Text } = Typography;
const { Option } = Select;
const DRILLDOWN_PAGE_SIZE = 20;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const formatAverageTime = (seconds) => {
    const numericSeconds = Number(seconds ?? 0);
    if (!Number.isFinite(numericSeconds)) return "00:00";
    const mins = Math.floor(Math.max(0, numericSeconds) / 60);
    const secs = Math.round(Math.max(0, numericSeconds) % 60);
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const formatMinuteValue = (value) => `${Number(value).toFixed(2)} min`;

const getPreviousDateRange = (dateRange = []) => {
    const [start, end] = dateRange;
    if (!start || !end) return [];

    const rangeDays = end.startOf("day").diff(start.startOf("day"), "day") + 1;
    const previousEnd = start.subtract(1, "day").endOf("day");
    const previousStart = previousEnd
        .subtract(rangeDays - 1, "day")
        .startOf("day");

    return [previousStart, previousEnd];
};

const normalizeChartSeries = (items = []) => {
    if (!Array.isArray(items)) return [];

    return items.map((item) => {
        const checkinStandard = Number(
            item?.checkinStandard ??
            item?.checkin_standard ??
            item?.checkinstandard ??
            0,
        );
        const walkinStandard = Number(
            item?.walkinStandard ??
            item?.walkin_standard ??
            item?.walkinstandard ??
            0,
        );

        const checkinExtended = Number(
            item?.checkinExtended ??
            item?.checkin_extended ??
            item?.checkinextended ??
            checkinStandard,
        );
        const walkinExtended = Number(
            item?.walkinExtended ??
            item?.walkin_extended ??
            item?.walkinextended ??
            walkinStandard,
        );

        return {
            label: item?.label || item?.bucket_label || item?.date || item?.period || "",
            checkinStandard,
            walkinStandard,
            checkinExtended,
            walkinExtended,
            checkinTransactions: Number(
                item?.checkinTransactions ??
                item?.checkin_transactions ??
                item?.checkintransactions ??
                0,
            ),
            walkinTransactions: Number(
                item?.walkinTransactions ??
                item?.walkin_transactions ??
                item?.walkintransactions ??
                0,
            ),
        };
    });
};

const getPercentChange = (current, previous) => {
    const currentValue = Number(current ?? 0);
    const previousValue = Number(previous ?? 0);

    if (!Number.isFinite(currentValue) || !Number.isFinite(previousValue)) {
        return 0;
    }

    if (previousValue === 0) {
        return currentValue === 0 ? 0 : 100;
    }

    return ((currentValue - previousValue) / Math.abs(previousValue)) * 100;
};

const formatPercentChange = (current, previous) => {
    const change = getPercentChange(current, previous);
    return `${change >= 0 ? "+" : ""}${change.toFixed(1)}%`;
};

const getInsightSummary = (response) => {
    const raw = response ?? {};
    const payload =
        raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)
            ? raw.data
            : raw;
    const summarySource =
        payload.summary || payload.data?.summary || payload.data || payload;
    const summary = Array.isArray(summarySource)
        ? summarySource[0] || {}
        : summarySource || {};

    return {
        total_checkin_count: Number(summary.total_checkin_count ?? 0),
        total_walkin_count: Number(summary.total_walkin_count ?? 0),
        total_other_count: Number(summary.total_other_count ?? 0),
        checkin_standard_avg_time: Number(
            summary.checkin_standard_avg_time ?? 0,
        ),
        checkin_extended_avg_time: Number(
            summary.checkin_extended_avg_time ?? 0,
        ),
        walkin_standard_avg_time: Number(
            summary.walkin_standard_avg_time ?? 0,
        ),
        walkin_extended_avg_time: Number(
            summary.walkin_extended_avg_time ?? 0,
        ),
    };
};

const buildApiInsightWidgets = (apiData = {}, previousApiData = {}) => {
    const metrics = apiData && typeof apiData === "object" ? apiData : {};
    const previousMetrics =
        previousApiData && typeof previousApiData === "object"
            ? previousApiData
            : {};

    const widgetDelta = (key, type) => {
        const delta = formatPercentChange(metrics[key], previousMetrics[key]);
        const current = Number(metrics[key] ?? 0);
        const previous = Number(previousMetrics[key] ?? 0);

        return {
            delta,
            direction: current >= previous ? "up" : "down",
            type,
        };
    };

    return [
        {
            key: "checkins",
            title: "Check-ins",
            value: Number(metrics.total_checkin_count ?? 0),
            ...widgetDelta("total_checkin_count", "count"),
            color: "#2f9e44",
        },
        {
            key: "walkins",
            title: "Walk-ins",
            value: Number(metrics.total_walkin_count ?? 0),
            ...widgetDelta("total_walkin_count", "count"),
            color: "#1c7ed6",
        },
        {
            key: "other",
            title: "Other",
            value: Number(metrics.total_other_count ?? 0),
            ...widgetDelta("total_other_count", "count"),
            color: "#f08c00",
        },
        {
            key: "checkin-standard",
            title: "Standard(90%) Avg Time",
            value: formatAverageTime(metrics.checkin_standard_avg_time),
            ...widgetDelta("checkin_standard_avg_time", "time"),
            color: "#1971c2",
        },
        {
            key: "walkin-standard",
            title: "Standard(90%) Avg Time",
            value: formatAverageTime(metrics.walkin_standard_avg_time),
            ...widgetDelta("walkin_standard_avg_time", "time"),
            color: "#4c6ef5",
        },
        {
            key: "checkin-extended",
            title: "Extended(10%) Avg Time",
            value: formatAverageTime(metrics.checkin_extended_avg_time),
            ...widgetDelta("checkin_extended_avg_time", "time"),
            color: "#e03131",
        },
        {
            key: "walkin-extended",
            title: "Extended(10%) Avg Time",
            value: formatAverageTime(metrics.walkin_extended_avg_time),
            ...widgetDelta("walkin_extended_avg_time", "time"),
            color: "#c2255c",
        },
    ];
};

const createRangeData = ({ range, granularity }) => {
    const values = [];
    const baseCheckin = 4.2;
    const baseWalkin = 5.1;
    const baseCheckinExtended = 10.8;
    const baseWalkinExtended = 11.2;

    if (range === "7d") {
        const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
        const checkinTransactions = [320, 350, 370, 360, 410, 390, 430];
        const walkinTransactions = [48, 52, 56, 55, 62, 58, 66];

        dayNames.forEach((label, index) => {
            const wave = Math.sin(index / 1.8) * 0.8;
            const checkinStandard = Number(
                (baseCheckin + wave + index * 0.08).toFixed(2),
            );
            const walkinStandard = Number(
                (
                    baseWalkin +
                    Math.sin(index / 2.2) * 0.9 +
                    index * 0.04
                ).toFixed(2),
            );
            const checkinExtended = Number(
                (
                    baseCheckinExtended +
                    Math.sin(index / 2.1 + 1.2) * 1.4 +
                    index * 0.15
                ).toFixed(2),
            );
            const walkinExtended = Number(
                (
                    baseWalkinExtended +
                    Math.cos(index / 2.5 + 0.7) * 1.2 +
                    index * 0.12
                ).toFixed(2),
            );

            values.push({
                label,
                checkinStandard,
                walkinStandard,
                checkinExtended,
                walkinExtended,
                checkinTransactions: checkinTransactions[index],
                walkinTransactions: walkinTransactions[index],
            });
        });
        return values;
    }

    if (range === "1m" && granularity === "daily") {
        for (let day = 1; day <= 30; day += 1) {
            const wave = Math.sin(day / 3.4) * 0.7;
            const checkinStandard = Number(
                (baseCheckin + wave + (day % 6) * 0.06).toFixed(2),
            );
            const walkinStandard = Number(
                (
                    baseWalkin +
                    Math.sin(day / 4.1) * 0.8 +
                    (day % 5) * 0.08
                ).toFixed(2),
            );
            const checkinExtended = Number(
                (
                    baseCheckinExtended +
                    Math.sin(day / 4.8 + 1.2) * 1.3 +
                    (day % 7) * 0.18
                ).toFixed(2),
            );
            const walkinExtended = Number(
                (
                    baseWalkinExtended +
                    Math.cos(day / 5 + 0.8) * 1.1 +
                    (day % 5) * 0.14
                ).toFixed(2),
            );
            const checkinTransactions = 180 + ((day * 7) % 90) + (day % 5) * 18;
            const walkinTransactions = 22 + ((day * 3) % 28) + (day % 4) * 3;

            values.push({
                label: `D${day}`,
                checkinStandard,
                walkinStandard,
                checkinExtended,
                walkinExtended,
                checkinTransactions,
                walkinTransactions,
            });
        }
        return values;
    }

    if (range === "1m" && granularity === "weekly") {
        const weeklyCheckins = [1200, 1300, 1380, 1420];
        const weeklyWalkins = [150, 175, 190, 205];

        ["W1", "W2", "W3", "W4"].forEach((label, index) => {
            const wave = Math.sin(index / 1.6) * 0.7;
            const checkinStandard = Number(
                (baseCheckin + wave + index * 0.16).toFixed(2),
            );
            const walkinStandard = Number(
                (
                    baseWalkin +
                    Math.sin(index / 2.3) * 0.75 +
                    index * 0.09
                ).toFixed(2),
            );
            const checkinExtended = Number(
                (
                    baseCheckinExtended +
                    Math.sin(index / 2.2 + 0.9) * 1.6 +
                    index * 0.25
                ).toFixed(2),
            );
            const walkinExtended = Number(
                (
                    baseWalkinExtended +
                    Math.cos(index / 2.5 + 0.7) * 1.4 +
                    index * 0.2
                ).toFixed(2),
            );
            values.push({
                label,
                checkinStandard,
                walkinStandard,
                checkinExtended,
                walkinExtended,
                checkinTransactions: weeklyCheckins[index],
                walkinTransactions: weeklyWalkins[index],
            });
        });
        return values;
    }

    const monthMap = {
        "1m": ["W1", "W2", "W3", "W4"],
        "3m": ["Jan", "Feb", "Mar"],
        "6m": ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    };
    const months = monthMap[range] || [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
    ];

    const monthlyCheckins = {
        "1m": [1200, 1300, 1380, 1420],
        "3m": [4200, 4500, 4700],
        "6m": [2800, 3100, 3400, 3600, 3900, 4200],
    };
    const monthlyWalkins = {
        "1m": [150, 175, 190, 205],
        "3m": [620, 680, 710],
        "6m": [360, 390, 430, 460, 500, 540],
    };

    const checkinSeries =
        monthlyCheckins[range] || Array(months.length).fill(0);
    const walkinSeries = monthlyWalkins[range] || Array(months.length).fill(0);

    months.forEach((label, index) => {
        const variance = Math.sin(index + 1) * 0.72;
        const checkinStandard = Number(
            (baseCheckin + variance + index * 0.12).toFixed(2),
        );
        const walkinStandard = Number(
            (baseWalkin + Math.sin(index / 1.6) * 0.7 + index * 0.1).toFixed(2),
        );
        const checkinExtended = Number(
            (
                baseCheckinExtended +
                Math.cos(index / 1.7 + 0.9) * 1.8 +
                index * 0.22
            ).toFixed(2),
        );
        const walkinExtended = Number(
            (
                baseWalkinExtended +
                Math.sin(index / 1.8 + 0.5) * 1.7 +
                index * 0.19
            ).toFixed(2),
        );
        values.push({
            label,
            checkinStandard,
            walkinStandard,
            checkinExtended,
            walkinExtended,
            checkinTransactions: checkinSeries[index],
            walkinTransactions: walkinSeries[index],
        });
    });

    return values;
};

const createWidgetState = (data) => {
    const checkinVolume = data.reduce(
        (sum, item) => sum + item.checkinTransactions,
        0,
    );
    const walkinVolume = data.reduce(
        (sum, item) => sum + item.walkinTransactions,
        0,
    );
    const otherVolume = Math.round(checkinVolume * 0.27);

    const avg = (key) => {
        const values = data.map((item) => item[key]);
        return values.reduce((sum, value) => sum + value, 0) / values.length;
    };

    const previousChunk = data.slice(
        0,
        Math.max(1, Math.ceil(data.length / 2)),
    );
    const currentChunk = data.slice(-Math.max(1, Math.ceil(data.length / 2)));

    const percentChange = (current, previous) => {
        if (!previous) return 0;
        return ((current - previous) / previous) * 100;
    };

    const currentCheckin =
        currentChunk.reduce((sum, item) => sum + item.checkinTransactions, 0) /
        currentChunk.length;
    const prevCheckin =
        previousChunk.reduce((sum, item) => sum + item.checkinTransactions, 0) /
        previousChunk.length;
    const currentWalkin =
        currentChunk.reduce((sum, item) => sum + item.walkinTransactions, 0) /
        currentChunk.length;
    const prevWalkin =
        previousChunk.reduce((sum, item) => sum + item.walkinTransactions, 0) /
        previousChunk.length;

    return [
        {
            key: "checkins",
            title: "Check-ins",
            value: checkinVolume,
            // suffix: "visits",
            delta: `${
                percentChange(currentCheckin, prevCheckin) >= 0 ? "+" : ""
            }${percentChange(currentCheckin, prevCheckin).toFixed(1)}%`,
            direction:
                percentChange(currentCheckin, prevCheckin) >= 0 ? "up" : "down",
            type: "count",
            color: "#2f9e44",
        },
        {
            key: "walkins",
            title: "Walk-ins",
            value: walkinVolume,
            // suffix: "visits",
            delta: `${
                percentChange(currentWalkin, prevWalkin) >= 0 ? "+" : ""
            }${percentChange(currentWalkin, prevWalkin).toFixed(1)}%`,
            direction:
                percentChange(currentWalkin, prevWalkin) >= 0 ? "up" : "down",
            type: "count",
            color: "#1c7ed6",
        },
        {
            key: "other",
            title: "Other",
            value: otherVolume,
            // suffix: "visits",
            delta: "+3.1%",
            direction: "up",
            type: "count",
            color: "#f08c00",
        },
        {
            key: "checkin-standard",
            title: "Standard(90%) Avg Time",
            value: formatAverageTime(avg("checkinStandard") * 60),
            // suffix: "mm:ss",
            delta: `${(
                (avg("checkinStandard") - avg("checkinExtended")) *
                -1
            ).toFixed(1)}s`,
            direction: "down",
            type: "time",
            color: "#1971c2",
        },
        {
            key: "walkin-standard",
            title: "Standard(90%) Avg Time",
            value: formatAverageTime(avg("walkinStandard") * 60),
            // suffix: "mm:ss",
            delta: `${(
                (avg("walkinStandard") - avg("walkinExtended")) *
                -1
            ).toFixed(1)}s`,
            direction: "down",
            type: "time",
            color: "#4c6ef5",
        },
        {
            key: "checkin-extended",
            title: "Extended(10%) Avg Time",
            value: formatAverageTime(avg("checkinExtended") * 60),
            // suffix: "mm:ss",
            delta: `+${(
                avg("checkinExtended") - avg("checkinStandard")
            ).toFixed(1)}m`,
            direction: "up",
            type: "time",
            color: "#e03131",
        },
        {
            key: "walkin-extended",
            title: "Extended(10%) Avg Time",
            value: formatAverageTime(avg("walkinExtended") * 60),
            // suffix: "mm:ss",
            delta: `+${(avg("walkinExtended") - avg("walkinStandard")).toFixed(
                1,
            )}m`,
            direction: "up",
            type: "time",
            color: "#c2255c",
        },
    ];
};

const baseWidgets = [
    {
        key: "checkins",
        title: "Check-ins",
        value: 1284,
        // suffix: "visits",
        delta: "+12.4%",
        direction: "up",
        type: "count",
        color: "#2f9e44",
    },
    {
        key: "walkins",
        title: "Walk-ins",
        value: 986,
        // suffix: "visits",
        delta: "+6.8%",
        direction: "up",
        type: "count",
        color: "#1c7ed6",
    },
    {
        key: "other",
        title: "Other",
        value: 327,
        // suffix: "visits",
        delta: "+3.1%",
        direction: "up",
        type: "count",
        color: "#f08c00",
    },
    {
        key: "checkin-standard",
        title: "Standard Avg Transaction Time",
        value: formatAverageTime(222),
        // suffix: "mm:ss",
        delta: "-18s",
        direction: "down",
        type: "time",
        color: "#1971c2",
    },
    {
        key: "walkin-standard",
        title: "Walking Standard Avg Transaction Time",
        value: formatAverageTime(268),
        // suffix: "mm:ss",
        delta: "-23s",
        direction: "down",
        type: "time",
        color: "#4c6ef5",
    },
    {
        key: "checkin-extended",
        title: "Check-in Extended Avg Transaction Time",
        value: formatAverageTime(316),
        // suffix: "mm:ss",
        delta: "+12s",
        direction: "up",
        type: "time",
        color: "#e03131",
    },
    {
        key: "walkin-extended",
        title: "Walking Extended Avg Transaction Time",
        value: formatAverageTime(340),
        // suffix: "mm:ss",
        delta: "+9s",
        direction: "up",
        type: "time",
        color: "#c2255c",
    },
];

const allWidgetOptions = [
    { key: "checkins", ...baseWidgets[0] },
    { key: "walkins", ...baseWidgets[1] },
    { key: "other", ...baseWidgets[2] },
    { key: "checkin-standard", ...baseWidgets[3] },
    { key: "walkin-standard", ...baseWidgets[4] },
    { key: "checkin-extended", ...baseWidgets[5] },
    { key: "walkin-extended", ...baseWidgets[6] },
    {
        key: "peak-hours",
        title: "Peak Hours",
        value: 18,
        suffix: "hrs",
        delta: "+2.5%",
        direction: "up",
        type: "count",
        color: "#12b886",
    },
    {
        key: "avg-queue-time",
        title: "Average Queue Time",
        value: formatAverageTime(186),
        // suffix: "mm:ss",
        delta: "-14s",
        direction: "down",
        type: "time",
        color: "#fd7e14",
    },
];

const chartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    return (
        <div className="performance-chart-tooltip">
            <div className="performance-chart-tooltip-title">{label}</div>
            {payload.map((entry) => (
                <div
                    key={entry.dataKey}
                    className="performance-chart-tooltip-row"
                >
                    <span
                        className="performance-chart-tooltip-dot"
                        style={{ background: entry.color }}
                    />
                    <span>{entry.name}</span>
                    <strong>{entry.value} min</strong>
                </div>
            ))}
        </div>
    );
};

const PerformanceInsights = () => {
    const { mode } = useThemeMode();
    const isDarkMode = mode === "dark";
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const [widgets, setWidgets] = useState(baseWidgets);
    const [selectedWidget, setSelectedWidget] = useState(null);
    const [chartSeries, setChartSeries] = useState([]);
    const [selectedRange, setSelectedRange] = useState("7d");
    const [selectedChartSettings, setSelectedChartSettings] = useState({
        checkin: { metric: "standard", granularity: "weekly" },
        walkin: { metric: "standard", granularity: "weekly" },
    });
    const [activeWidget, setActiveWidget] = useState("checkins");
    const [hotelOptions, setHotelOptions] = useState([]);
    const [selectedHotelIds, setSelectedHotelIds] = useState([]);
    const [drilldownRecords, setDrilldownRecords] = useState([]);
    const [drilldownTotal, setDrilldownTotal] = useState(0);
    const [drilldownLoading, setDrilldownLoading] = useState(false);
    const [drilldownPage, setDrilldownPage] = useState(1);
    const [drilldownSearch, setDrilldownSearch] = useState("");
    const [drilldownStatus, setDrilldownStatus] = useState("");
    const [drilldownSort, setDrilldownSort] = useState({
        field: "transaction_date",
        order: null,
    });
    const drilldownSectionRef = useRef(null);
    const [selectedDateRange, setSelectedDateRange] = useState([
        dayjs().subtract(6, "day").startOf("day"),
        dayjs().endOf("day"),
    ]);

    const getRangeDates = (range) => {
        const end = dayjs().endOf("day");

        switch (range) {
            case "7d":
                return [dayjs().subtract(6, "day").startOf("day"), end];
            case "1m":
                return [dayjs().subtract(1, "month").startOf("day"), dayjs().subtract(1, "month").endOf("month")];
            case "currentMonth":
                return [dayjs().startOf("month"), end];
            case "3m":
                return [dayjs().subtract(3, "month").startOf("day"), end];
            case "6m":
                return [dayjs().subtract(6, "month").startOf("day"), end];
            default:
                return [dayjs().subtract(6, "day").startOf("day"), end];
        }
    };

    const disabledCustomDate = (current) => {
        if (!current) return false;

        const minimumDate = dayjs().subtract(6, "month").startOf("day");
        const maximumDate = dayjs().endOf("day");

        return (
            current.isBefore(minimumDate, "day") ||
            current.isAfter(maximumDate, "day")
        );
    };

    useEffect(() => {
        const fetchHotels = async () => {
            if (!staff_id) return;

            try {
                const response = await HTTP("/dashboard/getHotels", {
                    staff_id,
                    hotel_type: "all",
                });

                const hotels = Array.isArray(response?.data?.data)
                    ? response.data.data
                    : Array.isArray(response?.data)
                    ? response.data
                    : [];

                setHotelOptions(hotels);
            } catch (error) {
                console.error("Failed to fetch hotel list", error);
                setHotelOptions([]);
            }
        };

        fetchHotels();
    }, [staff_id]);

    useEffect(() => {
        const fetchInsightsData = async () => {
            if (!staff_id) return;

            const startDate = selectedDateRange?.[0]?.format("YYYY-MM-DD");
            const endDate = selectedDateRange?.[1]?.format("YYYY-MM-DD");
            const previousDateRange = getPreviousDateRange(selectedDateRange);
            const previousStartDate = previousDateRange?.[0]?.format(
                "YYYY-MM-DD",
            );
            const previousEndDate = previousDateRange?.[1]?.format(
                "YYYY-MM-DD",
            );

            const requestInsights = (requestStartDate, requestEndDate) =>
                HTTP("/dashboard/getInsightsKioskCounts", {
                    staff_id,
                    hotel_ids:
                        selectedHotelIds.length > 0
                            ? selectedHotelIds
                            : undefined,
                    start_date: requestStartDate,
                    end_date: requestEndDate,
                });

            try {
                const [response, previousResponse] = await Promise.all([
                    requestInsights(startDate, endDate),
                    requestInsights(previousStartDate, previousEndDate),
                ]);

                const payload =
                    response?.data &&
                    typeof response.data === "object" &&
                    !Array.isArray(response.data)
                        ? response.data
                        : response ?? {};

                const chartData = Array.isArray(payload.chart_data)
                    ? payload.chart_data
                    : Array.isArray(payload.data?.chart_data)
                    ? payload.data.chart_data
                    : [];

                const normalizedSummary = getInsightSummary(response);
                const previousSummary = getInsightSummary(previousResponse);
                const normalizedChart = normalizeChartSeries(chartData);

                setWidgets(
                    buildApiInsightWidgets(normalizedSummary, previousSummary),
                );
                setChartSeries(normalizedChart);
            } catch (error) {
                console.error("Failed to fetch insights data", error);
                setWidgets(baseWidgets);
                setChartSeries([]);
            }
        };

        fetchInsightsData();
    }, [staff_id, selectedHotelIds, selectedDateRange]);

    const selectedHotels =
        selectedHotelIds.length > 0
            ? hotelOptions.filter((hotel) =>
                  selectedHotelIds.includes(String(hotel.hotel_id ?? hotel.id)),
              )
            : [];

    const selectedHotelChips =
        selectedHotels.length > 0
            ? selectedHotels.map((hotel) => {
                  const hotelName = hotel.hotel_name || hotel.name || "Unnamed hotel";
                  const city = hotel.city ? ` · ${hotel.city}` : "";
                  return `${hotelName}${city}`;
              })
            : ["All hotels"];

    const activeHotelFilter =
        selectedHotelIds.length > 0 ? selectedHotelIds : ["all"];

    const addableWidgets = allWidgetOptions.filter(
        (widget) => !widgets.some((item) => item.key === widget.key),
    );

    const transactionData = useMemo(
        () => generatePerformanceTransactions(selectedRange),
        [selectedRange],
    );

    const summaryData = useMemo(
        () =>
            summarizeTransactions(
                transactionData,
                selectedRange,
                selectedChartSettings.checkin.granularity,
            ),
        [
            transactionData,
            selectedRange,
            selectedChartSettings.checkin.granularity,
        ],
    );

    const visibleChartData = chartSeries.length ? chartSeries : [];

    const widgetChartData = useMemo(() => {
        return chartSeries.length ? chartSeries : [];
    }, [chartSeries]);

    const dynamicWidgets = useMemo(
        () => createWidgetState(widgetChartData),
        [widgetChartData],
    );

    const chartMetricConfig = {
        standard: {
            checkinName: "Check-in 90%",
            walkinName: "Walk-in 90%",
            checkinKey: "checkinStandard",
            walkinKey: "walkinStandard",
            checkinColor: "#2f9e44",
            walkinColor: "#1c7ed6",
        },
        extended: {
            checkinName: "Check-in 10%",
            walkinName: "Walk-in 10%",
            checkinKey: "checkinExtended",
            walkinKey: "walkinExtended",
            checkinColor: "#e03131",
            walkinColor: "#c2255c",
        },
    };

    const metricOptions = {
        standard: "Standard 90%",
        extended: "Extended 10%",
    };

    const finalWidgets = widgets.length ? widgets : dynamicWidgets;

    const widgetGroups = useMemo(() => {
        const source = finalWidgets.length ? finalWidgets : dynamicWidgets;
        const map = new Map(source.map((item) => [item.key, item]));

        const groups = [
            {
                key: "checkin-group",
                title: "Check-in",
                keys: ["checkins", "checkin-standard", "checkin-extended"],
            },
            {
                key: "walkin-group",
                title: "Walk-in",
                keys: ["walkins", "walkin-standard", "walkin-extended"],
            },
            {
                key: "other-group",
                title: "Other",
                keys: ["other"],
            },
        ];

        const grouped = groups
            .map((group) => ({
                key: group.key,
                title: group.title,
                items: group.keys.map((key) => map.get(key)).filter(Boolean),
            }))
            .filter((group) => group.items.length > 0);

        const leftovers = source.filter(
            (item) => !groups.some((group) => group.keys.includes(item.key)),
        );

        // if (leftovers.length) {
        //     grouped.push({
        //         key: "additional-insights",
        //         title: "Additional insights",
        //         items: leftovers,
        //     });
        // }

        return grouped;
    }, [dynamicWidgets, finalWidgets]);

    const renderGroupChart = (type) => {
        const isCheckIn = type === "checkin";
        const chartSettings = selectedChartSettings[type];
        const title = isCheckIn
            ? "Check-In Performance"
            : "Walk-In Performance";
        const color = isCheckIn ? "green" : "blue";
        const config = chartMetricConfig[chartSettings.metric];
        const lineKey = isCheckIn ? config.checkinKey : config.walkinKey;
        const lineName = isCheckIn ? config.checkinName : config.walkinName;
        const lineColor = isCheckIn ? config.checkinColor : config.walkinColor;
        const chartValues = visibleChartData.map((item) =>
            Number(item[lineKey] ?? 0),
        );
        const visibleMin = chartValues.length ? Math.min(...chartValues) : 0;
        const visibleMax = chartValues.length ? Math.max(...chartValues) : 10;

        return (
            <Card className="performance-insight-chart-card performance-insights-group-chart">
                <div className="performance-insight-chart-header">
                    <Title level={5}>{title}</Title>
                    <div className="performance-insight-chart-controls">
                        <div className="performance-insights-filter-group compact">
                            <Text className="performance-insights-filter-label">
                                View
                            </Text>
                            <Select
                                value={chartSettings.metric}
                                onChange={(metric) =>
                                    setSelectedChartSettings((previous) => ({
                                        ...previous,
                                        [type]: {
                                            ...previous[type],
                                            metric,
                                        },
                                    }))
                                }
                                style={{ width: 150 }}
                                size="small"
                            >
                                <Option value="standard">Standard 90%</Option>
                                <Option value="extended">Extended 10%</Option>
                            </Select>
                        </div>
                        <Tag color={color}>{metricOptions[chartSettings.metric]}</Tag>
                    </div>
                </div>
                <div className="performance-insight-chart-wrap">
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart
                            data={visibleChartData}
                            margin={{ top: 10, right: 16, left: 8, bottom: 10 }}
                        >
                            <CartesianGrid
                                strokeDasharray="0"
                                stroke={isDarkMode ? "#354156" : "#dfe3e8"}
                                vertical={false}
                            />
                            <XAxis
                                dataKey="label"
                                axisLine={false}
                                tickLine={false}
                                tick={{
                                    fill: isDarkMode ? "#b8c4d8" : "#5f6c7b",
                                    fontSize: 12,
                                }}
                            />
                            <YAxis
                                domain={[
                                    Math.max(2, Math.floor(visibleMin - 0.5)),
                                    Math.ceil(visibleMax + 0.7),
                                ]}
                                tickFormatter={(value) => `${value}min`}
                                axisLine={false}
                                tickLine={false}
                                tick={{
                                    fill: isDarkMode ? "#b8c4d8" : "#5f6c7b",
                                    fontSize: 12,
                                }}
                            />
                            <Tooltip content={chartTooltip} />
                            <Line
                                type="monotone"
                                dataKey={lineKey}
                                name={lineName}
                                stroke={lineColor}
                                strokeWidth={2.4}
                                dot={{
                                    r: 4,
                                    fill: lineColor,
                                    strokeWidth: 0,
                                }}
                                activeDot={{
                                    r: 5,
                                    fill: lineColor,
                                    strokeWidth: 0,
                                }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </Card>
        );
    };

    const activeWidgetData =
        finalWidgets.find((item) => item.key === activeWidget) ||
        finalWidgets[0];

    const handleWidgetSelect = (widgetKey) => {
        setActiveWidget(widgetKey);
        setDrilldownPage(1);

        window.requestAnimationFrame(() => {
            drilldownSectionRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        });
    };

    const drilldownGroup = activeWidget.startsWith("checkin")
        ? "checkin"
        : activeWidget.startsWith("walkin")
        ? "walkin"
        : "other";
    const drilldownDurationGroup = activeWidget.endsWith("-standard")
        ? "standard"
        : activeWidget.endsWith("-extended")
        ? "extended"
        : undefined;

    useEffect(() => {
        const fetchDrilldown = async () => {
            if (!staff_id || !selectedDateRange?.[0] || !selectedDateRange?.[1]) {
                return;
            }

            setDrilldownLoading(true);
            try {
                const response = await HTTP(
                    "/dashboard/getPerformanceInsightTransactions",
                    {
                        staff_id,
                        operation_group: drilldownGroup,
                        duration_group: drilldownDurationGroup,
                        hotel_ids:
                            selectedHotelIds.length > 0
                                ? selectedHotelIds
                                : undefined,
                        start_date: selectedDateRange[0].format("YYYY-MM-DD"),
                        end_date: selectedDateRange[1].format("YYYY-MM-DD"),
                        search: drilldownSearch.trim() || undefined,
                        status: drilldownStatus || undefined,
                        sort_by: drilldownSort.field,
                        sort_order:
                            drilldownSort.order === "ascend"
                                ? "asc"
                                : drilldownSort.order === "descend"
                                ? "desc"
                                : undefined,
                        limit: DRILLDOWN_PAGE_SIZE,
                        offset: (drilldownPage - 1) * DRILLDOWN_PAGE_SIZE,
                    },
                );

                const payload = response?.data || response || {};
                setDrilldownRecords(
                    Array.isArray(payload.result) ? payload.result : [],
                );
                setDrilldownTotal(Number(payload.total || 0));
            } catch (error) {
                console.error("Failed to fetch performance drill-down", error);
                setDrilldownRecords([]);
                setDrilldownTotal(0);
            } finally {
                setDrilldownLoading(false);
            }
        };

        fetchDrilldown();
    }, [
        drilldownDurationGroup,
        drilldownGroup,
        drilldownPage,
        drilldownSearch,
        drilldownSort,
        drilldownStatus,
        selectedDateRange,
        selectedHotelIds,
        staff_id,
    ]);

    useEffect(() => {
        setDrilldownPage(1);
    }, [
        activeWidget,
        drilldownSearch,
        drilldownSort,
        drilldownStatus,
        selectedDateRange,
        selectedHotelIds,
    ]);

    useEffect(() => {
        if (!transactionData.length) return;

        const checkinCount = transactionData.filter(
            (item) => item.type === "checkin",
        ).length;
        const walkinCount = transactionData.filter(
            (item) => item.type === "walkin",
        ).length;

        if (checkinCount >= walkinCount) {
            setActiveWidget("checkins");
            return;
        }

        setActiveWidget("walkins");
    }, [transactionData]);

    const drilldownColumns = [
        {
            title: "Date",
            dataIndex: "transaction_date",
            key: "transaction_date",
            sorter: true,
            sortOrder:
                drilldownSort.field === "transaction_date"
                    ? drilldownSort.order
                    : null,
            render: (value) =>
                value
                    ? new Date(value).toLocaleString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                    })
                    : "-",
        },
        {
            title: "Hotel",
            dataIndex: "hotel_name",
            key: "hotel_name",
        },
        {
            title: "Transaction",
            dataIndex: "transaction_id",
            key: "transaction_id",
        },
        {
            title: "Type",
            dataIndex: "operation_type",
            key: "operation_type",
            render: (value) => {
                const operationType = Number(value);
                const operationTypeLabels = {
                    1: { label: "Check-in", color: "green" },
                    2: { label: "Walk-in", color: "blue" },
                    3: { label: "Refund", color: "cyan" },
                    4: { label: "Check-out", color: "purple" },
                    5: { label: "Lost Key", color: "gold" },
                    6: { label: "Self Check-in", color: "geekblue" },
                    7: { label: "Mobile Check-in", color: "blue" },
                    8: { label: "Mini Kiosk", color: "magenta" },
                    9: { label: "Test", color: "volcano" },
                };
                const operation = operationTypeLabels[operationType] || {
                    label: "Other",
                    color: "orange",
                };

                return <Tag color={operation.color}>{operation.label}</Tag>;
            },
        },
        {
            title: "Guest",
            key: "guest",
            render: (_, record) =>
                `${record.first_name || ""} ${record.last_name || ""}`.trim() || "-",
        },
        {
            title: "Room",
            dataIndex: "room_number",
            key: "room_number",
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (value) => {
                const statusLabels = {
                    1: "In Progress",
                    2: "Aborted",
                    3: "Completed",
                };
                return statusLabels[Number(value)] || value || "-";
            },
        },
        {
            title: "Duration",
            dataIndex: "transaction_duration_seconds",
            key: "transaction_duration_seconds",
            sorter: true,
            sortOrder:
                drilldownSort.field === "transaction_duration_seconds"
                    ? drilldownSort.order
                    : null,
            render: (value) => {
                const totalSeconds = Number(value);
                if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
                    return "-";
                }

                const hours = Math.floor(totalSeconds / 3600);
                const minutes = Math.floor((totalSeconds % 3600) / 60);
                const seconds = Math.floor(totalSeconds % 60);
                return [hours, minutes, seconds]
                    .map((part) => String(part).padStart(2, "0"))
                    .join(":");
            },
        },
    ];

    const handleAddWidget = (value) => {
        if (!value) return;
        const selected = allWidgetOptions.find((item) => item.key === value);
        if (!selected) return;
        setWidgets((prev) => [...prev, selected]);
        setSelectedWidget(null);
    };

    return (
        <div
            className={`performance-insights-page ${
                isDarkMode ? "dark" : "light"
            }`}
        >
            <div className="performance-insights-filters">
                <Title
                   level={4}
                    style={{ marginRight: "auto" }}
                        color={isDarkMode ? "processing" : "blue"}                    >
                        Performance insights
                </Title>
                   
                <div className="performance-insights-filter-group">
                    <Text className="performance-insights-filter-label">
                        Hotel
                    </Text>
                    <Select
                        mode="multiple"
                        value={selectedHotelIds}
                        onChange={(values) => setSelectedHotelIds(values || [])}
                        allowClear
                        showSearch
                        maxTagCount={1}
                        maxTagTextLength={10}
                        optionLabelProp="label"
                        placeholder="Select hotels"
                        style={{ width: 220 }}
                        size="middle"
                        filterOption={(input, option) =>
                            String(option?.label ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                    >
                        {hotelOptions.map((hotel) => {
                            const hotelId = hotel.hotel_id ?? hotel.id;
                            const hotelName = hotel.hotel_name || hotel.name || "Unnamed hotel";
                            const city = hotel.city || "";
                            const optionLabel = city
                                ? `${hotelName} · ${city}`
                                : hotelName;

                            return (
                                <Option
                                    key={hotelId}
                                    value={String(hotelId)}
                                    label={optionLabel}
                                >
                                    <div className="performance-insights-hotel-option">
                                        <span>{hotelName}</span>
                                        {city ? <small>{city}</small> : null}
                                    </div>
                                </Option>
                            );
                        })}
                    </Select>
                </div>

                <div className="performance-insights-filter-group">
                    <Text className="performance-insights-filter-label">
                        Date range
                    </Text>
                    <Select
                        value={selectedRange}
                        onChange={(value) => {
                            if (value === "custom") {
                                setSelectedRange("custom");
                                return;
                            }

                            const dates = getRangeDates(value);
                            setSelectedRange(value);
                            setSelectedDateRange(dates);

                            setSelectedChartSettings((previous) => ({
                                checkin: {
                                    ...previous.checkin,
                                    granularity: "weekly",
                                },
                                walkin: {
                                    ...previous.walkin,
                                    granularity: "weekly",
                                },
                            }));
                        }}
                        style={{ width: 230 }}
                        size="middle"
                    >
                        <Option value="7d">Last 7 days</Option>
                        <Option value="1m">Last month</Option>
                        <Option value="currentMonth">Current month</Option>
                        <Option value="3m">Last 3 months</Option>
                        <Option value="6m">Last 6 months</Option>
                        <Option value="custom">Custom dates</Option>
                    </Select>

                    {selectedRange === "custom" && (
                        <DatePicker.RangePicker
                            value={selectedDateRange}
                            disabledDate={disabledCustomDate}
                            onChange={(dates) => {
                                if (!dates || !dates[0] || !dates[1]) return;
                                setSelectedDateRange(dates);
                                setSelectedRange("custom");
                            }}
                            format="YYYY-MM-DD"
                            size="middle"
                            style={{ marginLeft: 12, minWidth: 230 }}
                        />
                    )}
                    <CustomHeader compact profileOnly hideBack />
                </div>
            </div>

            {selectedHotelIds.length > 0 && (
                <div className="performance-insights-selected-hotel-row">
                    <div className="performance-insights-selected-hotel-list">
                        {selectedHotelChips.map((hotelLabel) => (
                            <Tag
                                key={hotelLabel}
                                className="performance-insights-selected-hotel-tag"
                                color={
                                    hotelLabel === "All hotels"
                                        ? "blue"
                                        : "processing"
                                }
                            >
                                {hotelLabel}
                            </Tag>
                        ))}
                    </div>
                </div>
            )}

            <div className="performance-insights-widget-groups">
                {widgetGroups.map((group) => {
                    const groupType =
                        group.key === "checkin-group" ? "checkin" : "walkin";

                    return (
                        <div
                            key={group.key}
                            className="performance-insights-widget-group"
                        >
                            <div className="performance-insights-group-header">
                                <Text
                                    className="performance-insights-group-title"
                                    level={6}
                                >
                                    {group.title}
                                </Text>
                            </div>
                            <Row
                                gutter={[12, 12]}
                                align="stretch"
                                className="performance-insights-group-row"
                            >
                                {group.items.map((item) => {
                                    const isPositive = item.direction === "up";
                                    const isTimeCard = item.type === "time";

                                    return (
                                        <Col
                                            xs={24}
                                            sm={12}
                                            lg={8}
                                            xl={8}
                                            key={item.key}
                                            className="performance-insights-group-cards-col"
                                        >
                                            <Card
                                                className={`performance-insight-card compact-card ${
                                                    activeWidget === item.key
                                                        ? "selected"
                                                        : ""
                                                }`}
                                                hoverable
                                                    onClick={() =>
                                                        handleWidgetSelect(item.key)
                                                    }
                                                style={{
                                                    borderTop: `4px solid ${item.color}`,
                                                }}
                                            >
                                                <div className="performance-insight-card-content">
                                                    <div className="performance-insight-label-row">
                                                        <AntTooltip title={item.title}>
                                                            <Text
                                                                className="performance-insight-label"
                                                                level={5}
                                                            >
                                                                {item.title}
                                                            </Text>
                                                        </AntTooltip>
                                                    </div>

                                                    <div className="performance-insight-metric-row">
                                                        {isTimeCard ? (
                                                            <div className="performance-insight-time">
                                                                <ClockCircleOutlined />
                                                                <span>
                                                                    {item.value}
                                                                </span>
                                                            </div>
                                                        ) : (
                                                            <div className="performance-insight-value">
                                                                <NumberOutlined />
                                                                {item.value.toLocaleString()}
                                                            </div>
                                                        )}
                                                        <div
                                                            className={`performance-insight-trend ${
                                                                isPositive
                                                                    ? "trend-up"
                                                                    : "trend-down"
                                                            }`}
                                                        >
                                                            {isPositive ? (
                                                                <ArrowUpOutlined />
                                                            ) : (
                                                                <ArrowDownOutlined />
                                                            )}
                                                            {item.delta}
                                                        </div>
                                                    </div>
                                                </div>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                            {group.key !== "other-group" && (
                                <div className="performance-insights-group-chart-col">
                                    {renderGroupChart(groupType)}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div ref={drilldownSectionRef} className="performance-insights-drilldown-anchor">
                <Row
                    gutter={[16, 16]}
                    className="performance-insights-drilldown-row"
                >
                <Col span={24}>
                    <Card className="performance-insight-table-card">
                        <div className="performance-insight-table-header">
                            <div>
                                <Text className="performance-insights-filter-label">
                                    Drilldown
                                </Text>
                                <Title level={5} style={{ margin: "6px 0 0" }}>
                                    {activeWidgetData?.title ||
                                        "Selected widget"}{" "}
                                    activity
                                </Title>
                            </div>
                            <Tag color={activeWidgetData?.color || "blue"}>
                                {drilldownTotal.toLocaleString()} {" "}
                                records
                            </Tag>
                        </div>

                        <div className="performance-insights-drilldown-controls">
                            <Input.Search
                                allowClear
                                placeholder="Search transactions, hotels..."
                                value={drilldownSearch}
                                onChange={(event) => setDrilldownSearch(event.target.value)}
                                style={{ maxWidth: 360, width: "100%" }}
                            />
                            <Select
                                allowClear
                                placeholder="Filter status"
                                value={drilldownStatus || undefined}
                                onChange={(value) => setDrilldownStatus(value || "")}
                                style={{ width: 180 }}
                            >
                                <Option value="1">In Progress</Option>
                                <Option value="2">Aborted</Option>
                                <Option value="3">Completed</Option>
                            </Select>
                        </div>

                        <Table
                            columns={drilldownColumns}
                            dataSource={drilldownRecords}
                            loading={drilldownLoading}
                            pagination={{
                                current: drilldownPage,
                                pageSize: DRILLDOWN_PAGE_SIZE,
                                total: drilldownTotal,
                                showSizeChanger: false,
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} of ${total} records`,
                            }}
                            onChange={(pagination, filters, sorter) => {
                                setDrilldownPage(pagination.current || 1);
                                if (!Array.isArray(sorter)) {
                                    setDrilldownSort({
                                        field: sorter.field || "transaction_date",
                                        order: sorter.order || null,
                                    });
                                }
                            }}
                            size="small"
                            rowKey="transaction_id"
                            className="performance-insight-table"
                            scroll={{ x: true }}
                        />
                    </Card>
                </Col>
                </Row>
            </div>
        </div>
    );
};

export { PerformanceInsights };
